import {useEffect, useState} from 'react';
import {Navigate, useLocation} from 'react-router-dom';
import {AppDispatch} from "../utils/store";
import {useDispatch, useSelector} from "react-redux";
import {selectIsLoggedIn, setUser} from "../utils/userSlice";
import {useApiService} from "../context/ApiServiceContext";
import User from "../models/User";
import {useTranslation} from "react-i18next";
import {normalizeLanguage} from "../utils/language";

interface RequireAuthProps {
    children: React.ReactNode;
    redirectTo: string;
    admin?: boolean;
}

const RequireAuth: React.FC<RequireAuthProps> = ({children, redirectTo, admin = false}) => {
    const apiService = useApiService();
    const dispatch: AppDispatch = useDispatch();
    const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const isLoggedIn = useSelector(selectIsLoggedIn);
    const location = useLocation();
    const { i18n } = useTranslation();

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const response = await apiService.api.get<User>('/user');
                const user = response.data
                dispatch(setUser(user))
                // console.log("user", user)
                setIsAdmin(user.admin);
                // The ACCOUNT language is authoritative on every load — not just
                // the first visit. The old "seed localStorage once" approach meant
                // an admin changing user.language did nothing on any browser that
                // had ever loaded the app. Normalized so a stray code can't split
                // text (Hebrew via fallback) from layout direction (LTR).
                const accountLanguage = normalizeLanguage(user.language) ?? 'he';
                localStorage.setItem('lang', accountLanguage);
                if (i18n.language !== accountLanguage) {
                    await i18n.changeLanguage(accountLanguage);
                }
            } catch (error) {
                setIsAdmin(false);
            } finally {
                setIsLoading(false)
            }
        };

        if (isLoggedIn !== undefined) {
            if (!isLoggedIn) {
                setIsAdmin(false);
                setIsLoading(false);
            } else {
                fetchUser()
            }
        }
    }, [dispatch, isLoggedIn]);

    if (isLoading) {
        // Loading state while checking admin status
        return null;
    } else if ((admin && !isAdmin) || (!admin && !isLoggedIn)) {
        return <Navigate to={redirectTo} state={{from: location}} replace/>;
    } else {
        return <>{children}</>;
    }
};

export default RequireAuth;
